import { type VariantProps, cva } from "class-variance-authority";
import type { ReactNode } from "react";

import { cn } from "../../lib/cn";
import { describeError } from "../../lib/errors";

const alertVariants = cva("rounded-lg border px-4 py-3 text-sm", {
  variants: {
    tone: {
      error:
        "border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300",
      success:
        "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
      info: "border-border bg-muted/50 text-foreground",
      warning:
        "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
    },
  },
  defaultVariants: { tone: "info" },
});

export function Alert({
  tone,
  className,
  children,
}: VariantProps<typeof alertVariants> & { className?: string; children: ReactNode }) {
  return (
    <div role={tone === "error" ? "alert" : "status"} className={cn(alertVariants({ tone }), className)}>
      {children}
    </div>
  );
}

/** Shows an API error in plain words, with the reference id support can look up in the logs. */
export function ErrorAlert({ error, className }: { error: unknown; className?: string }) {
  const { message, reference } = describeError(error);
  return (
    <Alert tone="error" {...(className === undefined ? {} : { className })}>
      <p>{message}</p>
      {reference !== undefined && <p className="mt-1 text-xs opacity-80">Reference: {reference}</p>}
    </Alert>
  );
}
